import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeamConfig } from '../../../context/TeamConfigContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { useTCoins } from '../../../context/TCoinsContext';
import { TEAM_CONFIG_PRICING } from '../../../data/defaultTeamConfigs';

const TeamConfigPage = () => {
  const navigate = useNavigate();
  const { locations, departments, wards, addLocation, addDepartment, addWard, deleteLocation, deleteDepartment, deleteWard, loading } = useTeamConfig();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const { getTotalTCoins, deductTCoins } = useTCoins();

  const [activeTab, setActiveTab] = useState('locations'); // 'locations' | 'departments' | 'wards'
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Check user plan (for now, default to FREE - can be extended later)
  const userPlan = currentUser?.plan || 'FREE';
  const totalTCoins = getTotalTCoins();
  const isLeader = currentTeam?.userRole === 'LEADER' || currentTeam?.userRole === 'DEPUTY_LEADER';

  const handleAdd = async () => {
    if (!newItem.trim()) {
      alert('กรุณากรอกข้อมูล');
      return;
    }

    // Check if user has Pro Plan or sufficient T Coins
    const cost = TEAM_CONFIG_PRICING.ADD_LOCATION; // Same cost for all types
    if (userPlan !== 'PRO' && totalTCoins < cost) {
      alert(`คุณต้องมี T Coins อย่างน้อย ${cost} เหรียญ หรือสมัคร Pro Plan`);
      return;
    }

    if (!window.confirm(`เพิ่ม "${newItem}"?\n${userPlan !== 'PRO' ? `ค่าใช้จ่าย: ${cost} T Coins` : 'ฟรี (Pro Plan)'}`)) {
      return;
    }

    setAdding(true);
    try {
      let result;
      if (activeTab === 'locations') {
        result = await addLocation(newItem.trim());
      } else if (activeTab === 'departments') {
        result = await addDepartment(newItem.trim());
      } else if (activeTab === 'wards') {
        result = await addWard(newItem.trim());
      }

      if (result.success) {
        // Deduct T Coins if not Pro
        if (userPlan !== 'PRO') {
          const deducted = await deductTCoins(currentUser.uid, 'real', cost, `เพิ่ม${activeTab === 'locations' ? 'ตำแหน่งเก็บอุปกรณ์' : activeTab === 'departments' ? 'แผนก' : 'หอผู้ป่วย'}`);
          if (!deducted) {
            alert('ไม่สามารถหัก T Coins ได้');
            return;
          }
        }
        alert('เพิ่มสำเร็จ');
        setNewItem('');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error adding item:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (itemName) => {
    if (!window.confirm(`ลบ "${itemName}"?`)) {
      return;
    }

    setDeleting(itemName);
    try {
      let result;
      if (activeTab === 'locations') {
        result = await deleteLocation(itemName);
      } else if (activeTab === 'departments') {
        result = await deleteDepartment(itemName);
      } else if (activeTab === 'wards') {
        result = await deleteWard(itemName);
      }

      if (result.success) {
        alert('ลบสำเร็จ');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const getCurrentItems = () => {
    if (activeTab === 'locations') return locations;
    if (activeTab === 'departments') return departments;
    if (activeTab === 'wards') return wards;
    return [];
  };

  const getTabLabel = () => {
    if (activeTab === 'locations') return 'ตำแหน่งเก็บอุปกรณ์';
    if (activeTab === 'departments') return 'แผนก';
    if (activeTab === 'wards') return 'หอผู้ป่วย';
    return '';
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  if (!isLeader) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            กลับ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/teams')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">จัดการตำแหน่งเก็บอุปกรณ์ของทีม {currentTeam.name}</h1>
          <div className="mt-4">
            <button
              onClick={() => navigate('/nurse/teams/beds')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              🛏️ จัดการเตียงผู้ป่วย
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('locations')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'locations'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📍 ตำแหน่งเก็บอุปกรณ์ ({locations.length})
            </button>
            <button
              onClick={() => setActiveTab('departments')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'departments'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              🏥 แผนก ({departments.length})
            </button>
            <button
              onClick={() => setActiveTab('wards')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'wards'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              🏨 หอผู้ป่วย ({wards.length})
            </button>
          </div>
        </div>

        {/* Add Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            เพิ่ม{getTabLabel()}ใหม่
          </h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAdd();
                }
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={`กรอก${getTabLabel()}ใหม่...`}
            />
            <button
              onClick={handleAdd}
              disabled={adding || !newItem.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              {adding ? 'กำลังเพิ่ม...' : 'เพิ่ม'}
            </button>
          </div>
          {userPlan !== 'PRO' && (
            <p className="text-xs text-gray-500 mt-2">
              💰 ค่าใช้จ่าย: {TEAM_CONFIG_PRICING.ADD_LOCATION} T Coins (Pro Plan ฟรี)
            </p>
          )}
        </div>

        {/* Items List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            รายการ{getTabLabel()} ({getCurrentItems().length})
          </h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : getCurrentItems().length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>ยังไม่มี{getTabLabel()}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {getCurrentItems().map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  <span className="text-gray-800">{item}</span>
                  <button
                    onClick={() => handleDelete(item)}
                    disabled={deleting === item}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition disabled:opacity-50"
                  >
                    {deleting === item ? 'กำลังลบ...' : 'ลบ'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamConfigPage;

